import type { BaseUIEvent } from '@base-ui/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type FocusEvent, useEffect, useState } from 'react';

import Check from '@/assets/svg/bx-check.svg?react';
import Clone from '@/assets/svg/bx-copy.svg?react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextInput,
} from '@/components/Form/inputs/InputGroup';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';

import type { TextInputProps } from '../TextInput';

export type CopyToClipboardTextInputProps = Omit<TextInputProps, 'className'> & {
  className?: string;
};

const CopyToClipboardTextInput = ({
  className,
  disabled,
  size = 'large',
  value,
  onFocus,
  ...rest
}: CopyToClipboardTextInputProps) => {
  const [copied, setCopied] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeoutId = window.setTimeout(() => {
        setCopied(false);
        setIsTooltipOpen(false);
      }, 3000);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [copied]);

  const handleFocus = (e: BaseUIEvent<FocusEvent<HTMLInputElement, Element>>) => {
    // Select the text so it can be easily copied
    e.target.select();

    if (onFocus) {
      onFocus(e);
    }
  };

  const handleCopy = () => {
    if (value) {
      setCopied(true);
      navigator.clipboard.writeText(value + '');
    }
  };

  return (
    <InputGroup size={size} className={className}>
      <InputGroupTextInput
        {...rest}
        size={size}
        value={value}
        disabled={disabled}
        onFocus={handleFocus}
        readOnly
      />
      <InputGroupAddon align="inline-end">
        <Tooltip open={copied || isTooltipOpen} onOpenChange={setIsTooltipOpen} disabled={disabled}>
          <TooltipTrigger
            render={
              <InputGroupButton
                className={copied ? '[&_svg]:size-7!' : ''}
                size={size === 'large' ? 'icon' : 'icon-small'}
                shape="round"
                disabled={disabled}
                aria-label={t`Copy text to clipboard`}
                onClick={handleCopy}
              >
                {copied ? <Check className="text-green-500" /> : <Clone />}
              </InputGroupButton>
            }
          />
          <TooltipContent>
            {copied ? <Trans>Copied!</Trans> : <Trans>Copy to clipboard</Trans>}
          </TooltipContent>
        </Tooltip>
      </InputGroupAddon>
    </InputGroup>
  );
};

export default CopyToClipboardTextInput;
